/**
 * English (EN) translations
 */

export const en = {
    // Common
    common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        add: 'Add',
        search: 'Search',
        filter: 'Filter',
        back: 'Back',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No',
        close: 'Close',
        submit: 'Submit',
        login: 'Log in',
        logout: 'Log out',
        register: 'Register',
        email: 'Email',
        password: 'Password',
        name: 'Name',
        phone: 'Phone',
        address: 'Address',
        date: 'Date',
        time: 'Time',
        status: 'Status',
        actions: 'Actions',
        details: 'Details',
        description: 'Description',
        notes: 'Notes',
        or: 'or',
        appName: 'ASP Service Center',
        operationFailed: 'Operation failed',
    },

    vehicleServiceStatus: {
        ordered: 'Ordered',
        inProgress: 'In Progress',
        waitingForParts: 'Waiting for Parts',
        completed: 'Completed',
        cancelled: 'Cancelled',
    },

    // Auth
    auth: {
        welcome: 'Welcome',
        signIn: 'Sign In',
        signUp: 'Sign Up',
        signOut: 'Sign Out',
        emailLabel: 'Email Address',
        emailPlaceholder: 'your@email.com',
        passwordLabel: 'Password',
        passwordPlaceholder: 'Enter password',
        fullNameLabel: 'Full Name',
        fullNamePlaceholder: 'John Doe',
        phoneLabel: 'Phone',
        phonePlaceholder: '+1 234 567 890',
        forgotPassword: 'Forgot password?',
        noAccount: "Don't have an account?",
        hasAccount: 'Already have an account?',
        createAccount: 'Create Account',
        signInButton: 'Sign In',
        signUpButton: 'Sign Up',
        invalidCredentials: 'Invalid credentials',
        userNotFound: 'User not found',
        emailInUse: 'Email already in use',
        weakPassword: 'Password is too weak',
        registrationSuccess: 'Registration successful',
        loginSuccess: 'Login successful',
        logoutSuccess: 'Logout successful',
        authenticationRequired: 'User must be authenticated',
        googleLoginFailed: 'Google login failed',
        signInWithGoogle: 'Sign in with Google',
        noAccountSignUp: "Don't have an account? Sign up",
        registrationFailed: 'Registration failed',
        confirmPassword: 'Confirm password',
        hasAccountSignIn: 'Already have an account? Sign in',
        forgotPasswordLink: 'Reset it',
        resetPassword: 'Password Reset',
        resetPasswordDescription: 'Enter your email and we will send you a link to reset your password.',
        resetPasswordButton: 'Send reset link',
        resetPasswordSending: 'Sending…',
        resetPasswordSent: 'A password reset link has been sent to',
        resetPasswordFailed: 'Failed to send email. Please check that the address is correct.',
        backToLogin: 'Back to login',
        verifyEmailTitle: 'Verify your email',
        verifyEmailDescription: 'We sent a verification link to {{email}}. Click the link in the email and then return here.',
        verifyEmailResend: 'Resend',
        verifyEmailResendCooldown: 'Resend ({{seconds}}s)',
        verifyEmailResendSuccess: 'Verification email has been resent.',
        verifyEmailCheck: 'I have verified – continue',
        verifyEmailNotYet: 'Your email has not been verified yet. Please check your inbox.',
        verifyEmailFailed: 'Failed to send verification email.',
    },

    // Navigation
    nav: {
        dashboard: 'Dashboard',
        profile: 'Profile',
        users: 'Users',
        settings: 'Settings',
        about: 'About',
        howItWorks: 'How It Works',
        support: 'Support',
        exports: 'Exports',
    },

    // Users
    user: {
        users: 'Users',
        userDetails: 'User Details',
        role: 'Role',
        roleCustomer: 'Customer',
        roleDealer: 'Dealer',
        roleAdmin: 'Administrator',
        createdAt: 'Registered',
        lastLogin: 'Last Login',
        active: 'Active',
        inactive: 'Inactive',
        updateRole: 'Update Role',
        deactivate: 'Deactivate',
        activate: 'Activate',
        userUpdated: 'User updated',
        myProfile: 'My Profile',
        profilePlaceholder: 'User profile will be implemented.',
        usersManagement: 'Users Management',
        usersManagementPlaceholder: 'Users management will be implemented.',
    },

    // Dashboard
    dashboard: {
        title: 'Dashboard',
        welcome: 'Welcome back',
        statistics: 'Statistics',
        newOrders: 'New Orders',
        ordersInProgress: 'Orders in Progress',
        completedOrders: 'Completed Orders',
        totalRevenue: 'Total Revenue',
        recentOrders: 'Recent Orders',
        pendingOrders: 'Pending Orders',
        todayOrders: 'Today\'s Orders',
        totalOrders: 'Total Orders',
    },

    // Notifications
    notification: {
        welcomeTitle: 'Welcome to ASP Service Center',
        welcomeMessage: 'Thank you for registering. You can now start using our services.',
        orderStatusChangeTitle: 'Order Status Changed',
        orderStatusChangeMessage: 'The status of your order #{orderId} has been changed.',
    },

    // Email
    email: {
        orderConfirmationSubject: 'Order Confirmation #{orderId}',
        orderConfirmationTitle: 'Service Order Confirmation',
        orderConfirmationGreeting: 'Hello {customerName},',
        orderConfirmationMessage: 'Your service order has been successfully created.',
        orderConfirmationDetails: 'Order Details:',
        orderConfirmationOrderNumber: 'Order Number:',
        orderConfirmationDevice: 'Device:',
        orderConfirmationDeviceType: 'Type:',
        orderConfirmationFollowUp: 'We will inform you about the next steps via email.',
        orderConfirmationSignature: 'Best regards,<br>ASP Service Center Team',

        orderStatusUpdateSubject: 'Order Update #{orderId}',
        orderStatusUpdateTitle: 'Order Status Update',
        orderStatusUpdateGreeting: 'Hello {customerName},',
        orderStatusUpdateMessage: 'We are informing you about the status change of your service order <strong>#{orderId}</strong>.',
        orderStatusUpdateNewStatus: 'New Status:',
        orderStatusUpdateNote: 'Note:',
        orderStatusUpdateSignature: 'Best regards,<br>ASP Service Center Team',

        orderStatusInProgress: 'Your order is currently being processed.',
        orderStatusWaitingForParts: 'Waiting for replacement parts for repair.',
        orderStatusCompleted: 'Service is completed and the device is ready for pickup.',
        orderStatusCancelled: 'Order has been cancelled.',
        orderStatusDefault: 'The status of your order has changed.',

        dailyReportSubject: 'Daily Report - ASP Service Center',
        dailyReportTitle: 'Daily Report',
        dailyReportGreeting: 'Hello,',
        dailyReportMessage: 'Here is the daily service center report:',
        dailyReportStatistics: 'Statistics:',
        dailyReportNewOrders: 'New orders today:',
        dailyReportPendingOrders: 'Pending orders:',
        dailyReportOrdersInProgress: 'Orders in progress:',
        dailyReportSignature: 'Best regards,<br>ASP Service Center System',
    },

    // Audit Log
    auditLog: {
        title: 'Audit log',
        subtitle: 'Overview of all system operations',
        filterDateFrom: 'Date from',
        filterDateTo: 'Date to',
        filterEntityType: 'Entity type',
        filterEntityId: 'Entity ID',
        filterActorUid: 'Actor UID',
        filterAll: 'All',
        filterApply: 'Filter',
        filterReset: 'Reset',
        colTimestamp: 'Timestamp',
        colActor: 'Actor',
        colOperation: 'Operation',
        colEntityType: 'Entity type',
        colEntityId: 'Entity ID',
        colAction: 'Action',
        empty: 'No records',
        loadMore: 'Load more',
        loadingMore: 'Loading…',
        before: 'Before',
        after: 'After',
        metadata: 'Metadata',
        noDetail: 'No additional information',
    },

    // Errors
    errors: {
        generic: 'Something went wrong',
        networkError: 'Network error',
        unauthorized: 'Unauthorized access',
        forbidden: 'Forbidden access',
        notFound: 'Not found',
        serverError: 'Server error',
        validationError: 'Validation error',
        requiredField: 'This field is required',
        invalidEmail: 'Invalid email',
        invalidPhone: 'Invalid phone number',
        passwordTooShort: 'Password is too short',
        passwordsDoNotMatch: 'Passwords do not match',
    },
};
